// --------------------------------------------------------------------------
//  Export builder data layer.
//
//  Turns the dashboard's live data into the building blocks of a composed
//  export. Three responsibilities live here, all pure (no JSX):
//
//    1. COMPONENT_LIBRARY + resolveSection(): the catalogue of selectable
//       data blocks and a resolver that returns the *real* numbers/rows for
//       each one. The same resolved block feeds both the on-screen preview
//       and the generated file, so what the customer sees is what they get.
//    2. PROVIDED_TEMPLATES + custom-template persistence: ready-made layouts
//       plus save / load / delete of the customer's own named layouts.
//    3. LARGE_TABLES + schedule persistence: the full row-level datasets that
//       download on their own, and the monthly-capped delivery schedules.
//
//  Postgres mapping:
//    - section copy / template definitions stay in code (product copy).
//    - custom templates  -> `user_export_templates` (id, user_id, org_id,
//      name, config_json, created_at).
//    - schedules         -> `user_exports_schedule` (id, user_id, org_id,
//      title, template_ref, format, recipients TEXT[], cadence, day_of_month,
//      enabled, next_send_at, created_by). cadence is capped at 'monthly' in
//      the API as well as here so a client can't post a faster schedule.
//    - large-table downloads -> streamed from the canonical tables; the
//      column lists below mirror the report views in data.js.
//
//  Integration hook: replace the localStorage helpers with RTK Query
//  mutations (`useSaveExportTemplateMutation`, `useScheduleExportMutation`)
//  and the resolvers with selectors over the report slices.
// --------------------------------------------------------------------------

import {
    USERS, SIMS, COURSES, COURSES_BY_TYPE, DEPTS, SEND_LOG, COURSE_SEND_LOG,
    HIGH_RISK_USERS, COURSE_USER_COMPLETIONS, PERIOD_DATA, CLICKS_BY_MONTH, courseType,
} from './data';
import {
    ORG_SCORE, ORG_BAND, BENCHMARK, ORG_TREND, TOP_RISK_USERS, ORG_FACTOR_STATS,
    SIGNALS, ACTIVE_USERS, bandFor,
} from './riskScore';
import { DWM_SUMMARY, DWM_BY_EMAIL, DWM_TREND } from './dwmData';

// -- Small format helpers --------------------------------------------------
const num = (n) => (n == null ? '' : Number(n).toLocaleString('en-GB'));
const pct = (n) => `${Math.round(n)}%`;
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

export function escapeCsv(value) {
    if (value == null) return '';
    const s = String(value);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function downloadBlob(content, mime, filename) {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function slugify(s) {
    return String(s || 'export').trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/gi, '').toLowerCase() || 'export';
}

// -- Component library -----------------------------------------------------
// `drillable` flags surfaces that have a row-level detail view; ticking the
// drill-down toggle appends that detail to the export. `feature` gates a
// block behind a tenancy flag so it disappears when the feature is off.
export const COMPONENT_LIBRARY = [
    { group: 'Home', items: [
        { id: 'home.risk-dial',  label: 'Risk score (gauge + benchmark)' },
        { id: 'home.risk-trend', label: 'Risk score trend (6 months)' },
        { id: 'home.top-risk',   label: 'Highest risk users table', drillable: true, drillLabel: 'Include per-user score breakdown' },
        { id: 'home.metrics',    label: 'Period metric strip (click rate / completion / engaged)' },
    ]},
    { group: 'Simulations', items: [
        { id: 'sims.metrics',      label: 'Sim metric strip (sends / click / report / flagged)' },
        { id: 'sims.clicks',       label: 'Clicks by month' },
        { id: 'sims.funnel',       label: 'Simulation engagement funnel' },
        { id: 'sims.by-sim',       label: 'Click rate by simulation', drillable: true, drillLabel: 'Include recipients per top sim' },
        { id: 'sims.device',       label: 'Clicks by device' },
        { id: 'sims.dept',         label: 'Click rate by group', drillable: true, drillLabel: 'Include users in each group' },
        { id: 'sims.all',          label: 'All simulations table', drillable: true, drillLabel: 'Include sim detail per row' },
        { id: 'sims.send-history', label: 'Send history table', drillable: true, drillLabel: 'Include sender + recipient detail' },
    ]},
    { group: 'Courses', items: [
        { id: 'courses.metrics',     label: 'Course metric strip' },
        { id: 'courses.by-course',   label: 'Completion rate by course', drillable: true, drillLabel: 'Include user completions per course' },
        { id: 'courses.by-type',     label: 'Completion rate by type' },
        { id: 'courses.outstanding', label: 'Outstanding users table', drillable: true, drillLabel: 'Include per-user assignment age' },
    ]},
    { group: 'Users', items: [
        { id: 'users.summary-table', label: 'Combined user summary table', drillable: true, drillLabel: 'Include per-user activity timeline' },
        { id: 'users.high-risk',     label: 'High risk users grouping', drillable: true, drillLabel: 'Include risk score breakdown' },
    ]},
    { group: 'Risk', items: [
        { id: 'risk.methodology', label: 'Risk score methodology (narrative)' },
        { id: 'risk.factors',     label: 'Risk score factor breakdown', drillable: true, drillLabel: 'Include per-factor users above industry' },
    ]},
    { group: 'Dark web monitoring', feature: 'darkWebMonitoring', items: [
        { id: 'dwm.summary',  label: 'DWM headline numbers' },
        { id: 'dwm.trend',    label: 'Breach trend (18 months)' },
        { id: 'dwm.by-email', label: 'Most-mentioned emails table', drillable: true, drillLabel: 'Include source + first-seen per breach' },
    ]},
    { group: 'Narrative', items: [
        { id: 'text.implication',  label: 'Implication statements' },
    ]},
];

// Flat lookup so the order list and generators can resolve a section by id
// without walking the grouped structure each time.
export const SECTION_INDEX = (() => {
    const map = {};
    for (const g of COMPONENT_LIBRARY) for (const it of g.items) map[it.id] = { ...it, group: g.group, feature: g.feature };
    return map;
})();

// -- Section resolvers -----------------------------------------------------
// Each returns a normalised block the preview + generators understand:
//   { kind: 'gauge',     value, band, sub }
//   { kind: 'metrics',   items: [{label, value, sub}] }
//   { kind: 'bars',      items: [{label, value, max}], unit }
//   { kind: 'table',     columns: [...], rows: [[...]], total }
//   { kind: 'narrative', paragraphs: [...] }  (paragraphs may be {h, body, action})
// `total` on a table is the full row count so a capped preview/PDF can say
// "showing 20 of N" and point the reader at the full download.
const deviceSplit = () => {
    let mobile = 0, desktop = 0;
    for (const e of SEND_LOG) { if (e.device === 'Mobile') mobile++; else desktop++; }
    return { mobile, desktop, total: mobile + desktop };
};

const flaggedSims = () => SIMS.filter((s) => s.flag);

// -- Live-aggregation helpers ---------------------------------------------
// Each resolver prefers `live` (the real server aggregations the dashboard
// pages use, uncapped) and falls back to the client-hydrated modules when a
// slice is absent (History / Data tables re-runs, or a query still in flight).
const rnd = (n) => Math.round(Number(n) || 0);
const rate = (numer, denom) => (denom ? Math.round((numer / denom) * 100) : 0);
const uname = (u) => (`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—');
const bandName = (score) => (score == null ? '—' : (bandFor(score)?.name || '—'));
const levelFromBand = (b) => (b === 'Critical' || b === 'High' ? 'high' : b === 'Medium' ? 'med' : 'low');
const riskFromClick = (r) => (r >= 0.3 ? 'high' : r >= 0.15 ? 'med' : 'low');

// SIGNALS <-> server field mapping for the risk-factor table (same order as
// the five weighted signals). `org` = per-org mean field on the `risk` agg;
// `user` = per-user field on the `users` (riskUsers) agg.
const FACTOR_FIELDS = [
    { sig: 'fail-rate',     org: 'factorFailRate',     user: 'failRate' },
    { sig: 'repeat',        org: 'factorRepeat',       user: 'repeat' },
    { sig: 'stage-depth',   org: 'factorStageDepth',   user: 'stageDepth' },
    { sig: 'training-debt', org: 'factorTrainingDebt', user: 'trainingDebt' },
    { sig: 'course-lag',    org: 'factorCourseLag',    user: 'courseLag' },
];

// Bucket the daily `trends` rows into the last six calendar months for a bars
// block (sum `field` per month).
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function bucketMonthly(trends, field) {
    const order = [];
    const map = {};
    for (const t of trends || []) {
        const key = String(t.day || '').slice(0, 7); // YYYY-MM
        if (key.length !== 7) continue;
        if (!(key in map)) { map[key] = 0; order.push(key); }
        map[key] += Number(t[field]) || 0;
    }
    return order.slice(-6).map((k) => ({ label: MONTH_ABBR[parseInt(k.slice(5), 10) - 1] || k, value: map[k] }));
}

const simTotals = (sims) => sims.reduce((a, s) => ({
    sent: a.sent + (s.sent || 0), clicks: a.clicks + (s.clicks || 0),
    reports: a.reports + (s.reports || 0), landings: a.landings + (s.landings || 0),
}), { sent: 0, clicks: 0, reports: 0, landings: 0 });

const SECTION_RESOLVERS = {
    // Benchmark anchors are gated behind the industryBenchmarking flag so the
    // export honours the same invariant as the on-screen snapshot card.
    'home.risk-dial': (opts = {}, live) => {
        if (live?.risk?.orgScore != null) {
            const score = live.risk.orgScore;
            const band = bandName(score);
            return {
                kind: 'gauge', value: score, band,
                sub: opts.benchmarkOn && live.benchmark
                    ? `Platform average ${live.benchmark.platformAvg} · top 25% at ${live.benchmark.top25}`
                    : `${band} band · scored 0-100, lower is better`,
            };
        }
        return {
            kind: 'gauge', value: ORG_SCORE, band: ORG_BAND?.name,
            sub: opts.benchmarkOn && BENCHMARK.platformAvg != null
                ? `Platform average ${BENCHMARK.platformAvg} · top 25% at ${BENCHMARK.top25}`
                : `${ORG_BAND?.name} band · scored 0-100, lower is better`,
        };
    },
    'home.risk-trend': (opts = {}, live) => {
        if (Array.isArray(live?.riskTrend) && live.riskTrend.length >= 3) {
            return { kind: 'bars', unit: 'score', items: live.riskTrend.map((p) => ({ label: String(p.day || '').slice(5), value: p.score, max: 100 })) };
        }
        return { kind: 'bars', unit: 'score', items: ORG_TREND.map((p) => ({ label: p.label, value: p.org, max: 100 })) };
    },
    'home.top-risk': (opts = {}, live) => {
        if (Array.isArray(live?.riskUsers)) {
            return {
                kind: 'table', columns: ['User', 'Department', 'Risk score', 'Band'],
                total: live.riskUsers.length,
                rows: [...live.riskUsers].sort((a, b) => b.score - a.score).map((u) => [uname(u), u.dept || 'Unknown', u.score, u.band]),
            };
        }
        return { kind: 'table', columns: ['User', 'Department', 'Risk score', 'Band'], total: TOP_RISK_USERS.length, rows: TOP_RISK_USERS.map((u) => [u.name, u.dept, u.finalScore, u.band?.name]) };
    },
    'home.metrics': (opts = {}, live) => {
        if (live?.summary?.periods) {
            const ps = live.summary.periods;
            const p = ps.find((x) => x.period === (opts.period || '12m')) || ps[ps.length - 1] || {};
            return { kind: 'metrics', items: [
                { label: 'Sim click rate', value: `${rate(p.clicks, p.sends)}%`, sub: `${num(p.clicks)} of ${num(p.sends)} sims` },
                { label: 'Course completion', value: `${rate(p.courseCompleted, p.courseSends)}%`, sub: `${num(p.courseCompleted)} of ${num(p.courseSends)}` },
                { label: 'Engaged users', value: num(live.summary.activeUsers), sub: 'Active this period' },
                { label: 'Report rate', value: `${rate(p.reports, p.sends)}%`, sub: 'Of sims delivered' },
            ] };
        }
        const d = PERIOD_DATA[opts.period] || PERIOD_DATA['6m'];
        return { kind: 'metrics', items: [
            { label: 'Sim click rate', value: `${d.simClickRate}%`, sub: d.simSub },
            { label: 'Course completion', value: `${d.courseCompletion}%`, sub: d.courseSub },
            { label: 'Engaged users', value: num(d.engagedUsers), sub: 'Active this period' },
            { label: 'Report rate', value: `${d.reportRate}%`, sub: 'Of sims delivered' },
        ] };
    },
    'sims.metrics': (opts = {}, live) => {
        if (Array.isArray(live?.sims)) {
            const t = simTotals(live.sims);
            const flagged = live.sims.filter((s) => s.sent >= 10 && s.clicks / s.sent >= 0.3).length;
            return { kind: 'metrics', items: [
                { label: 'Sims sent', value: num(t.sent), sub: `${live.sims.length} campaigns` },
                { label: 'Click rate', value: `${rate(t.clicks, t.sent)}%`, sub: `${num(t.clicks)} clicked` },
                { label: 'Report rate', value: `${rate(t.reports, t.sent)}%`, sub: 'Reported as phishing' },
                { label: 'Flagged sims', value: num(flagged), sub: 'Sent >=10 & click >=30%' },
            ] };
        }
        const d = PERIOD_DATA[opts.period] || PERIOD_DATA['6m'];
        return { kind: 'metrics', items: [
            { label: 'Sims sent', value: num(SEND_LOG.length), sub: `${SIMS.length} campaigns` },
            { label: 'Click rate', value: `${d.simClickRate}%`, sub: d.simSub },
            { label: 'Report rate', value: `${d.reportRate}%`, sub: 'Reported as phishing' },
            { label: 'Flagged sims', value: num(flaggedSims().length), sub: 'Hit a flag criterion' },
        ] };
    },
    'sims.clicks': (opts = {}, live) => {
        if (Array.isArray(live?.trends)) return { kind: 'bars', unit: 'clicks', items: bucketMonthly(live.trends, 'clicks') };
        return { kind: 'bars', unit: 'clicks', items: (CLICKS_BY_MONTH[opts.period] || CLICKS_BY_MONTH['6m']).map((b) => ({ label: b.label, value: b.n })) };
    },
    'sims.funnel': (opts = {}, live) => {
        if (Array.isArray(live?.sims)) {
            const t = simTotals(live.sims);
            return { kind: 'bars', unit: 'events', items: [
                { label: 'Delivered', value: t.sent, max: t.sent },
                { label: 'No click', value: Math.max(0, t.sent - t.clicks), max: t.sent },
                { label: 'Clicked', value: t.clicks, max: t.sent },
                { label: '2nd stage', value: t.landings, max: t.sent },
                { label: 'Reported', value: t.reports, max: t.sent },
            ] };
        }
        const d = PERIOD_DATA[opts.period] || PERIOD_DATA['6m'];
        const [noClick, clicked, second, reported] = d.funnelSegments;
        return { kind: 'bars', unit: 'users', items: [
            { label: 'Delivered', value: d.funnelSent, max: d.funnelSent },
            { label: 'No click', value: noClick, max: d.funnelSent },
            { label: 'Clicked', value: clicked, max: d.funnelSent },
            { label: '2nd stage', value: second, max: d.funnelSent },
            { label: 'Reported', value: reported, max: d.funnelSent },
        ] };
    },
    'sims.by-sim': (opts = {}, live) => {
        if (Array.isArray(live?.sims)) {
            const rows = [...live.sims].filter((s) => s.sent > 0)
                .sort((a, b) => (b.clicks / b.sent) - (a.clicks / a.sent))
                .map((s) => [s.simName, `${rate(s.clicks, s.sent)}%`, cap(riskFromClick(s.clicks / s.sent)), num(s.sent), `${rate(s.reports, s.sent)}%`]);
            return { kind: 'table', columns: ['Simulation', 'Click rate', 'Risk', 'Sent', 'Report rate'], total: live.sims.length, rows };
        }
        return {
            kind: 'table', columns: ['Simulation', 'Click rate', 'Risk', 'Sent', 'Report rate'],
            total: SIMS.length,
            rows: [...SIMS].sort((a, b) => parseInt(b.click) - parseInt(a.click)).map((s) => [s.name, s.click, cap(s.lv), num(s.sent), `${s.reportRate}%`]),
        };
    },
    'sims.device': (opts = {}, live) => {
        if (Array.isArray(live?.simDevices)) {
            const tot = live.simDevices.reduce((a, d) => a + (d.clicks || 0), 0);
            return { kind: 'bars', unit: 'clicks', items: live.simDevices.map((d) => ({ label: d.device, value: d.clicks, max: tot })) };
        }
        const d = deviceSplit();
        return { kind: 'bars', unit: 'clicks', items: [
            { label: 'Desktop', value: d.desktop, max: d.total },
            { label: 'Mobile', value: d.mobile, max: d.total },
        ] };
    },
    'sims.dept': (opts = {}, live) => {
        if (Array.isArray(live?.leagues)) {
            const rows = live.leagues.filter((r) => r.dimType === 'department' && r.name !== 'Unknown')
                .sort((a, b) => b.users - a.users)
                .map((r) => [r.name, `${rnd((r.failRate || 0) * 100)}%`, rnd(r.avgRisk), num(r.users), cap(levelFromBand(bandName(rnd(r.avgRisk))))]);
            return { kind: 'table', columns: ['Group', 'Click rate', 'Risk score', 'Users', 'Level'], total: rows.length, rows };
        }
        return { kind: 'table', columns: ['Group', 'Click rate', 'Risk score', 'Users', 'Level'], total: DEPTS.length, rows: DEPTS.map((d) => [d.name, `${d.click}%`, d.risk, num(d.users), cap(d.lv)]) };
    },
    'sims.all': (opts = {}, live) => {
        if (Array.isArray(live?.sims)) {
            const rows = live.sims.map((s) => [s.simName, cap(riskFromClick(s.sent ? s.clicks / s.sent : 0)), `${rate(s.clicks, s.sent)}%`, num(s.sent), `${rate(s.reports, s.sent)}%`, (s.sent >= 10 && s.clicks / s.sent >= 0.3) ? 'Yes' : 'No']);
            return { kind: 'table', columns: ['Simulation', 'Risk', 'Click rate', 'Sent', 'Report rate', 'Flagged'], total: live.sims.length, rows };
        }
        return { kind: 'table', columns: ['Simulation', 'Level', 'Click rate', 'Sent', 'Difficulty', 'Flagged'], total: SIMS.length, rows: SIMS.map((s) => [s.name, cap(s.lv), s.click, num(s.sent), `${s.diff}/3`, s.flag ? 'Yes' : 'No']) };
    },
    'sims.send-history': (opts = {}, live) => {
        if (Array.isArray(live?.sims) && Array.isArray(SEND_LOG) && SEND_LOG.length) {
            const total = simTotals(live.sims).sent;
            return {
                kind: 'table', columns: ['Recipient', 'Simulation', 'Status', 'Date sent', 'Device', 'Source'],
                total,
                rows: SEND_LOG.slice(0, 50).map((e) => [e.email, e.simName, e.status.replace('_', ' '), e.dateSent, e.device, e.source]),
            };
        }
        return { kind: 'table', columns: ['Recipient', 'Simulation', 'Status', 'Date sent', 'Device', 'Source'], total: SEND_LOG.length, rows: SEND_LOG.map((e) => [e.email, e.simName, e.status.replace('_', ' '), e.dateSent, e.device, e.source]) };
    },
    'courses.metrics': (opts = {}, live) => {
        if (live?.courseDetail?.kpis && Array.isArray(live?.courses)) {
            const k = live.courseDetail.kpis;
            return { kind: 'metrics', items: [
                { label: 'Courses', value: num(live.courses.length), sub: 'In catalogue' },
                { label: 'Assignments', value: num(k.sent), sub: 'Sent this window' },
                { label: 'Completions', value: num(k.completed), sub: 'Marked complete' },
                { label: 'Overall rate', value: `${k.completionPct}%`, sub: 'Completion' },
            ] };
        }
        const sent = COURSE_SEND_LOG.length;
        const completed = COURSE_SEND_LOG.filter((c) => c.dateCompleted).length;
        return { kind: 'metrics', items: [
            { label: 'Courses', value: num(COURSES.length), sub: 'In catalogue' },
            { label: 'Assignments', value: num(sent), sub: 'Sent this window' },
            { label: 'Completions', value: num(completed), sub: 'Marked complete' },
            { label: 'Overall rate', value: pct(sent === 0 ? 0 : (completed / sent) * 100), sub: 'Completion' },
        ] };
    },
    'courses.by-course': (opts = {}, live) => {
        if (Array.isArray(live?.courses)) {
            return {
                kind: 'table', columns: ['Course', 'Type', 'Assigned', 'Completed', 'Rate'],
                total: live.courses.length,
                rows: live.courses.map((c) => [c.courseName, courseType(c.courseName), num(c.sent), num(c.completed), `${rate(c.completed, c.sent)}%`]),
            };
        }
        return { kind: 'table', columns: ['Course', 'Type', 'Assigned', 'Completed', 'Rate'], total: COURSES.length, rows: COURSES.map((c) => [c.name, c.type, num(c.sent), num(c.completed), `${c.pct}%`]) };
    },
    'courses.by-type': (opts = {}, live) => {
        if (Array.isArray(live?.courses)) {
            const m = {};
            const order = [];
            for (const c of live.courses) {
                const t = courseType(c.courseName);
                if (!(t in m)) { m[t] = { sent: 0, completed: 0 }; order.push(t); }
                m[t].sent += c.sent || 0; m[t].completed += c.completed || 0;
            }
            return { kind: 'bars', unit: '% complete', items: order.map((t) => ({ label: t, value: rate(m[t].completed, m[t].sent), max: 100 })) };
        }
        return { kind: 'bars', unit: '% complete', items: COURSES_BY_TYPE.map((t) => ({ label: t.name, value: t.pct, max: 100 })) };
    },
    'courses.outstanding': (opts = {}, live) => {
        if (Array.isArray(live?.courseDetail?.outstandingUsers)) {
            const src = live.courseDetail.outstandingUsers;
            return {
                kind: 'table', columns: ['User', 'Department', 'Assigned', 'Completed', 'Rate'],
                total: src.length,
                rows: src.map((u) => { const completed = (u.sent || 0) - (u.outstanding || 0); return [uname(u), u.dept || 'Unknown', num(u.sent), num(completed), `${rate(completed, u.sent)}%`]; }),
            };
        }
        const rows = COURSE_USER_COMPLETIONS.filter((u) => u.pct < 100).sort((a, b) => a.pct - b.pct);
        return { kind: 'table', columns: ['User', 'Department', 'Assigned', 'Completed', 'Rate'], total: rows.length, rows: rows.map((u) => [`${u.firstName} ${u.lastName}`, u.dept, num(u.sent), num(u.completed), `${u.pct}%`]) };
    },
    'users.summary-table': (opts = {}, live) => {
        if (Array.isArray(live?.roster)) {
            return {
                kind: 'table', columns: ['User', 'Department', 'Location', 'Risk', 'Level', 'Completion'],
                total: live.roster[0]?.total ?? live.roster.length,
                rows: live.roster.map((u) => [uname(u), u.dept || 'Unknown', u.location || 'Unknown', u.riskScore ?? '—', cap(levelFromBand(u.band)), `${u.completionPct ?? 0}%`]),
            };
        }
        return { kind: 'table', columns: ['User', 'Department', 'Location', 'Risk', 'Level', 'Completion'], total: USERS.length, rows: USERS.map((u) => [u.name, u.dept, u.location, u.scored ? u.risk : 'Unscored', u.lv ? cap(u.lv) : '-', `${u.comp}%`]) };
    },
    'users.high-risk': (opts = {}, live) => {
        if (Array.isArray(live?.riskUsers)) {
            return {
                kind: 'table', columns: ['User', 'Department', 'Sent', 'Clicked', '2nd stage', 'Reported'],
                total: live.riskUsers.length,
                rows: live.riskUsers.map((u) => [uname(u), u.dept || 'Unknown', num(u.sent), num(u.clicked), num(u.secondStage), num(u.reported)]),
            };
        }
        return { kind: 'table', columns: ['User', 'Department', 'Sent', 'Clicked', '2nd stage', 'Reported'], total: HIGH_RISK_USERS.length, rows: HIGH_RISK_USERS.map((u) => [`${u.firstName} ${u.lastName}`, u.dept, num(u.sent), num(u.clicked), num(u.secondStage), num(u.reported)]) };
    },
    'risk.methodology': (opts = {}, live) => {
        const score = live?.risk?.orgScore != null ? live.risk.orgScore : ORG_SCORE;
        const band = live?.risk?.orgScore != null ? bandName(score) : ORG_BAND?.name;
        return { kind: 'narrative', paragraphs: [
            { h: 'How the risk score is built', body: `Five weighted behavioural signals over a rolling 6-month window produce a 0-100 score. Lower is better. The current org score is ${score} (${band}).` },
            ...SIGNALS.map((s) => ({ h: `${s.label} (${s.weight}%)`, body: `${cap(s.meaning)}. ${s.description}` })),
        ] };
    },
    'risk.factors': (opts = {}, live) => {
        if (live?.risk) {
            const rows = FACTOR_FIELDS.map(({ sig, org }) => {
                const s = SIGNALS.find((x) => x.id === sig) || {};
                const mean = Number(live.risk[org]) || 0;
                const meanLabel = s.anchor?.format === 'days' ? `${(mean * 30).toFixed(1)}d` : pct(mean * 100);
                return [s.label, `${s.weight}%`, meanLabel, (mean * (s.weight || 0)).toFixed(1)];
            });
            return { kind: 'table', columns: ['Factor', 'Weight', 'Org mean', 'Avg contribution'], total: rows.length, rows };
        }
        return { kind: 'table', columns: ['Factor', 'Weight', 'Org mean', 'Avg contribution'], total: ORG_FACTOR_STATS.length, rows: ORG_FACTOR_STATS.map((f) => [f.label, `${f.weight}%`, pct(f.componentMean * 100), (f.avgContribution).toFixed(1)]) };
    },
    'dwm.summary': () => ({
        kind: 'metrics', items: [
            { label: 'Total breaches', value: num(DWM_SUMMARY.totalBreaches), sub: 'Mentions found' },
            { label: 'Emails breached', value: num(DWM_SUMMARY.emailsBreached), sub: `of ${num(DWM_SUMMARY.emailsScanned)} scanned` },
            { label: 'Credential leaks', value: num(DWM_SUMMARY.credentialBreaches), sub: 'Email + password' },
            { label: 'Email-only', value: num(DWM_SUMMARY.emailOnlyBreaches), sub: 'Address exposed' },
        ],
    }),
    'dwm.trend': () => ({
        kind: 'bars', unit: 'breaches',
        items: DWM_TREND.slice(-12).map((m) => ({ label: m.label, value: m.creds + m.emailOnly })),
    }),
    'dwm.by-email': () => ({
        kind: 'table', columns: ['Email', 'Department', 'Mentions', 'With creds', 'Sources', 'Latest'],
        total: DWM_BY_EMAIL.length,
        rows: DWM_BY_EMAIL.map((e) => [e.email, e.dept, num(e.mentions), num(e.withCreds), num(e.sources), e.latest]),
    }),
    'text.implication': (opts = {}, live) => {
        const score = (live?.risk?.orgScore ?? ORG_SCORE) ?? 0;
        const critical = Array.isArray(live?.riskUsers)
            ? live.riskUsers.filter((u) => u.band === 'Critical').length
            : TOP_RISK_USERS.filter((u) => u.band?.name === 'Critical').length;
        const active = live?.risk?.scoredUsers ?? ACTIVE_USERS;
        const paragraphs = [];
        // Peer comparison only when a real anchor exists - never against null.
        const peerAvg = BENCHMARK.industryAvg ?? BENCHMARK.platformAvg;
        const peerLabel = BENCHMARK.industryAvg != null ? 'industry' : 'platform';
        if (opts.benchmarkOn && peerAvg != null) {
            const gap = score - peerAvg;
            paragraphs.push({ h: null, body: gap > 0
                ? `You are more of a risk than the average peer: your score sits ${Math.abs(gap)} points above the ${peerLabel} average of ${peerAvg}.`
                : `You are outperforming peers: your score sits ${Math.abs(gap)} points below the ${peerLabel} average of ${peerAvg}.` });
        }
        paragraphs.push({ h: null, body: `${num(critical)} users are in the Critical band out of ${num(active)} active. Targeting them moves the org score faster than broad-based training.` });
        return { kind: 'narrative', paragraphs };
    },
};

// `opts` (e.g. { benchmarkOn }) lets a resolver respect tenancy feature flags
// without each one reaching into the flag store itself.
export function resolveSection(id, opts = {}, live = null) {
    const fn = SECTION_RESOLVERS[id];
    return fn ? fn(opts, live) : { kind: 'narrative', paragraphs: [{ h: null, body: 'No preview available for this section.' }] };
}

// Build the full ordered, resolved section list the preview + generators
// consume. `withDrill` is preserved so generators can append row detail; `opts`
// flows through to the resolvers (feature-flag aware).
export function assembleSections(selectedIds, drillIds, opts = {}, live = null) {
    const drill = drillIds instanceof Set ? drillIds : new Set(drillIds || []);
    return selectedIds
        .map((id) => SECTION_INDEX[id])
        .filter(Boolean)
        .map((it) => ({ ...it, withDrill: drill.has(it.id), block: resolveSection(it.id, opts, live) }));
}

// -- Provided templates ----------------------------------------------------
// Ready-made layouts. `sections` is the ordered id list; `drill` the subset
// that should default to including row-level detail.
export const PROVIDED_TEMPLATES = [
    {
        id: 'board-pack', name: 'Board pack', format: 'PDF',
        title: 'Board security report',
        description: 'One-page narrative for non-technical board members.',
        sections: ['home.risk-dial', 'home.risk-trend', 'text.implication', 'home.top-risk'],
        drill: [],
    },
    {
        id: 'exec-review', name: 'Exec review', format: 'PowerPoint',
        title: 'Executive security review',
        description: 'Slide-per-theme deck for the leadership meeting.',
        sections: ['home.metrics', 'home.risk-dial', 'sims.funnel', 'sims.by-sim', 'courses.by-type'],
        drill: [],
    },
    {
        id: 'monthly-ops', name: 'Monthly ops review', format: 'Excel',
        title: 'Monthly operations review',
        description: 'Operational detail for the security team.',
        sections: ['sims.metrics', 'sims.clicks', 'sims.by-sim', 'sims.dept', 'courses.metrics', 'courses.by-course', 'users.high-risk'],
        drill: ['sims.by-sim', 'sims.dept'],
    },
    {
        id: 'compliance', name: 'Compliance evidence', format: 'PDF',
        title: 'Compliance evidence pack',
        description: 'Training completion and methodology for auditors.',
        sections: ['courses.metrics', 'courses.by-course', 'courses.outstanding', 'risk.methodology', 'risk.factors'],
        drill: ['courses.outstanding'],
    },
    {
        id: 'risk-deep-dive', name: 'Risk deep-dive', format: 'Excel',
        title: 'Risk score deep-dive',
        description: 'Full factor breakdown and highest-risk users.',
        sections: ['home.risk-dial', 'risk.factors', 'risk.methodology', 'home.top-risk', 'users.high-risk'],
        drill: ['home.top-risk', 'users.high-risk'],
    },
];

// -- Custom template persistence -------------------------------------------
// Server-side since the ReportLayouts store gained SK EXPORT_TEMPLATES: the
// builder and scheduled-exports pages read/write via reportLayout.service
// (GET/PUT /dashboard/export-templates/{tenancyId}), so templates are
// per-tenancy, shared across browsers, and never leak between tenancies.

// -- Large tables ----------------------------------------------------------
// Full row-level datasets the customer can download on their own, in any of
// the four formats, without composing a document. Columns mirror the report
// views; `getRows` returns plain arrays aligned to `columns`.
export const LARGE_TABLES = [
    {
        id: 'users', label: 'All users', source: 'Users',
        description: 'Every user with risk score, level and completion.',
        columns: ['Name', 'Email', 'Department', 'Location', 'Country', 'Risk', 'Level', 'Completion', 'Disabled'],
        getRows: () => USERS.map((u) => [u.name, u.email, u.dept, u.location, u.country, u.scored ? u.risk : 'Unscored', u.lv ? cap(u.lv) : '-', `${u.comp}%`, u.disabled ? 'Yes' : 'No']),
    },
    {
        id: 'send-history', label: 'Simulation send history', source: 'Simulations',
        description: 'Per-recipient send events with status and device.',
        columns: ['Email', 'Simulation', 'Status', 'Date sent', 'Device', 'Source', 'Comms type', 'Department'],
        getRows: () => SEND_LOG.map((e) => [e.email, e.simName, e.status.replace('_', ' '), e.dateSent, e.device, e.source, e.commsType, e.dept]),
    },
    {
        id: 'course-sends', label: 'Course send log', source: 'Courses',
        description: 'Per-user course assignments and completion dates.',
        columns: ['Email', 'Course', 'Type', 'Department', 'Date sent', 'Source', 'Completed'],
        getRows: () => COURSE_SEND_LOG.map((c) => [c.email, c.courseName, c.courseType, c.dept, c.dateSent, c.source, c.dateCompleted || '']),
    },
    {
        id: 'high-risk', label: 'High risk users', source: 'Users',
        description: 'Users ranked by clicks and second-stage failures.',
        columns: ['Name', 'Department', 'Location', 'Sent', 'Clicked', '2nd stage', 'Reported'],
        getRows: () => HIGH_RISK_USERS.map((u) => [`${u.firstName} ${u.lastName}`, u.dept, u.location, u.sent, u.clicked, u.secondStage, u.reported]),
    },
    {
        id: 'all-sims', label: 'All simulations', source: 'Simulations',
        description: 'Every campaign with click rate, difficulty and flags.',
        columns: ['Simulation', 'Level', 'Click rate', 'Sent', 'Report rate', 'Difficulty', 'Flagged'],
        getRows: () => SIMS.map((s) => [s.name, cap(s.lv), s.click, s.sent, `${s.reportRate}%`, `${s.diff}/3`, s.flag ? 'Yes' : 'No']),
    },
    {
        id: 'course-completions', label: 'Per-user course completion', source: 'Courses',
        description: 'Completion percentage for every active user.',
        columns: ['Name', 'Department', 'Assigned', 'Completed', 'Rate'],
        getRows: () => COURSE_USER_COMPLETIONS.map((u) => [`${u.firstName} ${u.lastName}`, u.dept, u.sent, u.completed, `${u.pct}%`]),
    },
    {
        id: 'dwm-by-email', label: 'Dark web mentions by email', source: 'Dark web monitoring', feature: 'darkWebMonitoring',
        description: 'Breach mentions per email with credential exposure.',
        columns: ['Email', 'Name', 'Department', 'Mentions', 'With creds', 'Sources', 'Latest'],
        getRows: () => DWM_BY_EMAIL.map((e) => [e.email, e.name, e.dept, e.mentions, e.withCreds, e.sources, e.latest]),
    },
];

// -- Schedule persistence --------------------------------------------------
// Cadence is capped at monthly: weekly / daily are deliberately not offered
// so an export can fire at most once a month. Quarterly and annually sit
// below that ceiling.
export const CADENCES = [
    { id: 'monthly',   label: 'Monthly',   months: 1, note: 'Most frequent allowed' },
    { id: 'quarterly', label: 'Quarterly', months: 3 },
    { id: 'annually',  label: 'Annually',  months: 12 },
];

const SCHEDULE_KEY = 'rbx-export-schedules';

export function loadSchedules() {
    try {
        const v = JSON.parse(localStorage.getItem(SCHEDULE_KEY));
        return Array.isArray(v) ? v : [];
    } catch { return []; }
}

function persistSchedules(list) {
    try { localStorage.setItem(SCHEDULE_KEY, JSON.stringify(list)); } catch { /* ignore */ }
    return list;
}

// Next send is the next occurrence of `dayOfMonth` at-or-after today, then
// stepped forward by the cadence interval. Uses real wall-clock time because
// scheduling is forward-looking, unlike the data window which is anchored.
export function computeNextSend(cadence, dayOfMonth, from = new Date()) {
    const months = (CADENCES.find((c) => c.id === cadence) || CADENCES[0]).months;
    const day = Math.min(Math.max(1, dayOfMonth || 1), 28);
    const next = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), day));
    if (next <= from) next.setUTCMonth(next.getUTCMonth() + months);
    return next.toISOString().slice(0, 10);
}

export function saveSchedule(entry) {
    const list = loadSchedules();
    const id = entry.id || `sch-${Date.now()}-${list.length + 1}`;
    const nextSend = computeNextSend(entry.cadence, entry.dayOfMonth);
    const next = [...list.filter((s) => s.id !== id), { enabled: true, ...entry, id, nextSend }];
    return persistSchedules(next);
}

export function deleteSchedule(id) {
    return persistSchedules(loadSchedules().filter((s) => s.id !== id));
}

export function toggleSchedule(id) {
    return persistSchedules(loadSchedules().map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)));
}

// -- Export history --------------------------------------------------------
// A log of every export the customer has actually pulled (downloaded or sent),
// powering the Export history page. Each entry stores enough to re-download:
// documents keep their section recipe, tables keep their id + format.
//
// Postgres mapping: `user_exports` (id, user_id, org_id, kind, via, title,
// format, config_json, row_count, created_at). The page reads it back ordered
// by created_at desc.
const HISTORY_KEY = 'rbx-export-history';
const HISTORY_CAP = 200;

export function loadExportHistory() {
    try {
        const v = JSON.parse(localStorage.getItem(HISTORY_KEY));
        return Array.isArray(v) ? v : [];
    } catch { return []; }
}

export function logExport(entry) {
    const list = loadExportHistory();
    const row = { id: `exp-${Date.now()}-${list.length + 1}`, at: new Date().toISOString(), ...entry };
    const next = [row, ...list].slice(0, HISTORY_CAP);
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
}

export function clearExportHistory() {
    try { localStorage.removeItem(HISTORY_KEY); } catch { /* ignore */ }
    return [];
}

export const FORMATS = ['PDF', 'PowerPoint', 'Excel', 'CSV'];
export const TABLE_FORMATS = ['CSV', 'Excel', 'JSON', 'PDF'];

// Selectable data periods for a composed export. Ids match the dashboard API's
// period values (and the PERIOD_DATA / CLICKS_BY_MONTH fallback keys), so the
// builder's queries and the client fallbacks stay on the same window.
export const EXPORT_PERIODS = [
    { id: '30d', label: '30 days' },
    { id: '90d', label: '90 days' },
    { id: '6m',  label: '6 months' },
    { id: '12m', label: '12 months' },
];
export const periodLabel = (id) => (EXPORT_PERIODS.find((p) => p.id === id) || {}).label || id;

// Shared TextField styling for the export builder. MUI's default size="small"
// padding (8.5px) plus a 12px font reads thin next to the portal's own inputs;
// this bumps the box to a comfortable ~35px, rounds to the portal's 6px radius,
// fills with var(--surface2) and matches the teal focus ring. Covers text,
// email, number and multiline fields, so individual fields drop their per-field
// fontSize overrides. All values resolve inside .rbx-root.
export const INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        fontFamily: 'Inter, sans-serif',
        fontSize: '12px',
        backgroundColor: 'var(--surface2)',
    },
    '& .MuiOutlinedInput-input': {
        fontSize: '12px',
        lineHeight: '1.4',
        padding: '9px 12px',
        minHeight: '18px',
        boxSizing: 'border-box',
    },
    '& .MuiOutlinedInput-root.MuiInputBase-multiline': { padding: '9px 12px' },
    '& .MuiOutlinedInput-root.MuiInputBase-multiline .MuiOutlinedInput-input': { padding: 0 },
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
        WebkitAppearance: 'none', margin: 0,
    },
    '& .MuiInputLabel-root': { fontSize: '12px', color: 'var(--muted)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--teal)' },
    // Resting border matches the portal's .rep-* inputs (var(--border2)) rather
    // than MUI's default; focus brightens to teal.
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--border2)', borderWidth: '1px',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--teal)', borderWidth: '1px',
    },
};
